<template>
  <div v-if="currentItem" class="gallery-container">
    <div class="gallery-header">
      <div class="gallery-avatar">
        <img
          v-if="currentItem.sender_avatar_url"
          :src="currentItem.sender_avatar_url"
          class="gallery-avatar-img"
        />
        <Avatar
          v-else
          :username="currentItem.sender_name"
          class="gallery-name-img"
        />
        <div class="sender-name">{{ currentItem.sender_name }}</div>
      </div>
      <div class="gallery-controls">
        <woot-button
          class="controls-btn"
          icon="gallery-zoom-in"
          variant="smooth"
          fill-color="#A0A1A2"
          view-box="0 0 20 20"
          @click="zoomIn"
        />
        <woot-button
          class="controls-btn"
          icon="gallery-zoom-out"
          variant="smooth"
          fill-color="#A0A1A2"
          view-box="0 0 20 20"
          @click="zoomOut"
        />
        <woot-button
          class="controls-btn"
          icon="gallery-rotate-cc"
          variant="smooth"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.8"
          fill-color="none"
          view-box="0 0 24 24"
          stroke-color="#A0A1A2"
          @click="rotateCounterclockwise"
        />
        <woot-button
          class="controls-btn"
          icon="gallery-rotate-cw"
          variant="smooth"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.8"
          fill-color="none"
          view-box="0 0 24 24"
          stroke-color="#A0A1A2"
          @click="rotateClockwise"
        />
        <woot-button
          class="controls-btn"
          icon="gallery-flip-y"
          variant="smooth"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.8"
          fill-color="none"
          view-box="0 0 20 22"
          stroke-color="#A0A1A2"
          @click="rotateY"
        />
        <woot-button
          class="controls-btn"
          icon="gallery-flip-x"
          variant="smooth"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.8"
          fill-color="none"
          view-box="0 0 24 24"
          stroke-color="#A0A1A2"
          @click="rotateX"
        />
        <woot-button
          class="controls-btn"
          icon="gallery-download"
          variant="smooth"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          fill-color="none"
          view-box="0 0 20 20"
          stroke-color="#A0A1A2"
          @click="openInNewTab"
        />
        <woot-button
          class="controls-btn d-none"
          icon="gallery-download"
          variant="smooth"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          fill-color="none"
          view-box="0 0 20 20"
          stroke-color="#A0A1A2"
          @click="download"
        />
      </div>
      <div class="reset-actions">
        <input
          v-if="records.length > 1"
          class="controls-input"
          readonly
          type="text"
          :value="imageIndex"
        />
        <div class="controls-btn reset-btn" @click="reset">
          {{ $t('GALLERY.RESET') }}
        </div>
        <woot-button
          class="controls-btn"
          icon="gallery-dismiss"
          variant="smooth"
          fill-color="#A0A1A2"
          view-box="0 0 18 18"
          @click="closeGallery"
        />
      </div>
    </div>

    <div class="gallery-content" @click="closeGallery">
      <button
        v-if="records.length > 1"
        class="gallery-nav-button gallery-prev"
        @click.stop="prev"
      >
        &lt;
      </button>
      <button
        v-if="records.length > 1"
        class="gallery-nav-button gallery-next"
        @click.stop="next"
      >
        &gt;
      </button>
      <div
        v-if="currentItem.file_type === 'image'"
        class="image-wrapper"
        :style="imageStyle"
        @mousedown="handleMouseDown"
        @mousemove="handleMouseMove"
        @mouseup="handleMouseUp"
        @mouseleave="handleMouseUp"
        @click.stop
      >
        <img :src="currentItem.data_url" alt="Gallery Item" />
      </div>
      <div v-else class="video-wrapper" :style="videoStyle" @click.stop>
        <video :src="currentItem.data_url" controls />
      </div>
    </div>
  </div>
</template>

<script>
import Avatar from 'dashboard/components/widgets/Avatar';
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';

export default {
  components: {
    Avatar,
  },
  mixins: [alertMixin],
  data() {
    return {
      zoom: 1,
      rotationX: 0,
      rotationY: 0,
      rotationAngle: 0,
      disableTransition: false,
      isDragging: false,
      startX: 0,
      startY: 0,
      translateX: 0,
      translateY: 0,
      draggingFrame: null,
    };
  },
  computed: {
    ...mapGetters({
      currentIndex: 'mediaGallery/getActiveRecordIndex',
      records: 'mediaGallery/getRecords',
    }),

    imageIndex() {
      return `${this.currentIndex + 1} / ${this.records.length}`;
    },

    currentItem() {
      return this.records[this.currentIndex];
    },

    isImage() {
      return (
        this.currentItem.includes('http') && !this.currentItem.includes('.mp4')
      );
    },

    isVideo() {
      return this.currentItem.includes('.mp4');
    },

    imageStyle() {
      return {
        transform: `translate(${this.translateX}px, ${this.translateY}px) scale(${this.zoom}) rotateX(${this.rotationX}deg) rotateY(${this.rotationY}deg) rotate(${this.rotationAngle}deg)`,
        transformOrigin: 'center center',
        display: 'inline-block',
        transition: this.disableTransition ? 'none' : 'transform 0.3s',
      };
    },

    videoStyle() {
      return {
        transform: `scale(${this.zoom}) rotateX(${this.rotationX}deg) rotateY(${this.rotationY}deg) rotate(${this.rotationAngle}deg)`,
      };
    },
  },

  mounted() {
    this.$el.addEventListener('wheel', this.handleScrollZoom, {
      passive: false,
    });
    document.addEventListener('keydown', this.handleKeydown);
  },

  beforeDestroy() {
    this.$el.removeEventListener('wheel', this.handleScrollZoom);
    document.removeEventListener('keydown', this.handleKeydown);
  },

  methods: {
    closeGallery() {
      this.$store.commit('mediaGallery/resetGalleryView');
    },

    handleKeydown(event) {
      if (event.key === 'ArrowRight') {
        this.next();
      } else if (event.key === 'ArrowLeft') {
        this.prev();
      } else if (event.key === 'Escape') {
        this.closeGallery();
      }
    },

    handleScrollZoom(event) {
      event.preventDefault();
      if (event.deltaY < 0) {
        this.zoomIn();
      } else {
        this.zoomOut();
      }
    },

    handleMouseDown(event) {
      this.isDragging = true;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.dragOffsetX = this.translateX;
      this.dragOffsetY = this.translateY;
      this.disableTransition = true;
      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);
    },

    handleMouseMove(event) {
      if (!this.isDragging) return;
      this.mouseX = event.clientX;
      this.mouseY = event.clientY;
      if (!this.draggingFrame) {
        this.draggingFrame = requestAnimationFrame(() => {
          const deltaX = this.mouseX - this.startX;
          const deltaY = this.mouseY - this.startY;
          this.translateX = this.dragOffsetX + deltaX;
          this.translateY = this.dragOffsetY + deltaY;
          this.draggingFrame = null;
        });
      }
    },

    handleMouseUp() {
      this.isDragging = false;
      cancelAnimationFrame(this.draggingFrame);
      this.draggingFrame = null;
      this.disableTransition = false;
      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);
    },

    reset() {
      this.zoom = 1;
      this.rotationX = 0;
      this.rotationY = 0;
      this.rotationAngle = 0;
      this.translateX = 0;
      this.translateY = 0;
    },

    next() {
      if (this.currentIndex < this.records.length - 1) {
        this.$store.commit(
          'mediaGallery/setActiveRecordIndex',
          this.currentIndex + 1
        );
      } else {
        this.$store.commit('mediaGallery/setActiveRecordIndex', 0);
      }
      this.reset();
    },

    prev() {
      if (this.currentIndex > 0) {
        this.$store.commit(
          'mediaGallery/setActiveRecordIndex',
          this.currentIndex - 1
        );
      } else {
        this.$store.commit(
          'mediaGallery/setActiveRecordIndex',
          this.records.length - 1
        );
      }
      this.reset();
    },

    zoomIn() {
      this.zoom += 0.2;
    },

    zoomOut() {
      if (this.zoom > 0.4) this.zoom -= 0.2;
    },

    rotateX() {
      this.rotationX = this.rotationX ? 0 : 180;
    },

    rotateY() {
      this.rotationY = this.rotationY ? 0 : 180;
    },

    rotateClockwise() {
      this.rotationAngle += 45;
      if (this.rotationAngle === 405) this.resetRotationInstantly();
    },

    rotateCounterclockwise() {
      this.rotationAngle -= 45;
      if (this.rotationAngle === -405) this.resetRotationInstantly();
    },

    resetRotationInstantly() {
      this.disableTransition = true;
      this.rotationAngle = 0;
      setTimeout(() => {
        this.disableTransition = false;
      }, 0);
    },

    async openInNewTab() {
      const fileUrl = this.currentItem.data_url;

      if (!fileUrl) {
        this.showAlert(
          this.$t('GALLERY.DOWNLOAD_FAILED') + ': Missing file URL',
          'error'
        );
        return;
      }

      window.open(fileUrl, '_blank');
    },

    async download() {
      try {
        const response = await fetch(this.currentItem.data_url, {
          method: 'GET',
          credentials: 'omit',
          mode: 'cors',
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const filename = this.getFileName(this.currentItem.data_url);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        this.showAlert(
          ` ${this.$t('GALLERY.DOWNLOAD_FAILED')}: ${error.message}`,
          'error'
        );
      }
    },

    getFileName(url) {
      return url
        .split('/')
        .pop()
        .split('?')[0];
    },
  },
};
</script>

<style lang="scss" scoped>
.gallery-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  .gallery-header {
    position: absolute;
    top: 0;
    left: 0;
    background-color: #121316;
    height: 68px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px;
    z-index: 11;
    .gallery-avatar {
      display: flex;
      align-items: center;
      position: relative;
      .gallery-avatar-img {
        width: 45px;
        height: 45px;
        border-radius: var(--border-radius-rounded);
      }
      .gallery-name-img {
        height: 45px;
        width: 45px;
        border-radius: 50%;
      }
    }
    .sender-name {
      font-weight: 500;
      font-size: 16px;
      color: #a0a1a2;
      padding-left: 12px;
      text-transform: capitalize;
    }

    .gallery-controls {
      display: flex;
      justify-content: center;
      gap: 15px;
      position: relative;
      z-index: 10;
      transition: all 0.3s ease-in-out;
    }
    .controls-btn {
      font-size: 16px;
      background-color: #202022;
      color: #a0a1a2;
      border: none;
      border-radius: 5px;
      cursor: pointer;
      transition: background-color 0.3s, transform 0.2s;
    }
    .controls-btn:hover {
      background-color: #2f2f33;
      transform: scale(1.1);
    }
    .reset-actions {
      display: flex;
      justify-content: center;
      gap: 15px;
      position: relative;
      z-index: 10;
      transition: all 0.3s ease-in-out;
      .reset-btn {
        font-size: 15px;
        font-weight: 500;
        text-align: center;
        padding: 8px 16px;
      }
    }
    .controls-input {
      width: 73px;
      margin-bottom: 0;
      text-align: center;
      height: 40px;
      color: #ffffff;
      background: #202022;
      border: none;
    }
  }

  .d-none {
    display: none;
  }

  .gallery-content {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    padding-top: 68px;
    .gallery-nav-button {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 45px;
      height: 45px;
      background: rgba(0, 0, 0);
      color: #a0a1a2;
      border: none;
      border-radius: 50%;
      display: flex;
      z-index: 11;
      justify-content: center;
      align-items: center;
      font-size: 24px;
      cursor: pointer;
      transition: background 0.3s ease, transform 0.2s ease;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    }

    .gallery-nav-button:hover {
      background: rgba(27, 26, 26, 0.9);
      transform: translateY(-50%) scale(1.1);
    }

    .gallery-nav-button:active {
      transform: translateY(-50%) scale(0.95);
    }

    .gallery-prev {
      left: 20px;
    }

    .gallery-next {
      right: 20px;
    }
  }
  .image-wrapper,
  .video-wrapper {
    position: relative;
    display: inline-block;
    object-fit: contain;
  }

  .image-wrapper img,
  .video-wrapper video {
    max-width: 90vw;
    max-height: 80vh;
    min-width: 45vw;
    min-height: 40vh;
    object-fit: contain;
  }

  .image-wrapper {
    transform-origin: center center;
    user-select: none;
    overflow: hidden;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: grab;
  }

  .image-wrapper img {
    max-width: 90vw;
    max-height: 80vh;
    object-fit: contain;
    pointer-events: none;
  }
}
</style>
